/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import DiscussionListItem, { type IDiscussionListItemAttrs } from 'flarum/forum/components/DiscussionListItem';
import avatar from 'flarum/common/helpers/avatar';
import humanTime from 'flarum/common/utils/humanTime';
import LinkButton from 'flarum/common/components/LinkButton';
import ArticleRating from './ArticleRating';
import Dropdown from 'flarum/common/components/Dropdown';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import { components } from '@glowingblue-orell-thalia-common';

const { ExpandableContent } = components;
import Link from 'flarum/common/components/Link';
import ReviewControls from '../utils/ReviewControls';
import ReviewItemCover from './ReviewItemCover';
import type User from 'flarum/common/models/User';
import type Mithril from 'mithril';

export interface ReviewItemAttrs extends IDiscussionListItemAttrs {
	/** Wraps the snippet in an `ExpandableContent`. Defaults to `true` via `initAttrs`. */
	expandable?: boolean;
	/** Omits the cover image entirely. */
	noCover?: boolean;
	/** Set when an ancestor already links to the review, so the cover must not nest a link. */
	linkAll?: boolean;
}

export default class ReviewItem extends DiscussionListItem<ReviewItemAttrs> {
	static initAttrs(attrs: ReviewItemAttrs) {
		super.initAttrs(attrs);
		if (!attrs.params) {
			attrs.params = {};
		}
		if (attrs.expandable === undefined) {
			attrs.expandable = true;
		}
	}

	oninit(vnode: Mithril.Vnode<ReviewItemAttrs, this>) {
		super.oninit(vnode);
	}

	elementAttrs() {
		const original = super.elementAttrs();
		return {
			...original,
			className: `${original.className} ReviewItem`,
		};
	}

	view() {
		const discussion = this.attrs.discussion;
		// `false` when the relation is absent, which both `avatar()` and the translator tolerate.
		const user = discussion.user() as User;

		const discussionControls = DiscussionControls.controls(discussion, this);
		const reviewControls = ReviewControls.controls(discussion, this);
		discussionControls.merge(reviewControls);
		const controls = discussionControls.toArray();

		const attrs = this.elementAttrs();

		if (this.attrs.params.q) {
			// @ts-expect-error `escapeRegExp` is never imported, so this branch would throw a
			// ReferenceError — unreached, because `initAttrs` defaults `params` to `{}`. Importing
			// it from `flarum/common/utils/escapeRegExp` is the fix, and a behaviour change.
			const phrase = escapeRegExp(this.attrs.params.q);
			this.highlightRegExp = new RegExp(phrase + '|' + phrase.trim().replace(/\s+/g, '|'), 'gi');
		}
		const firstPost = discussion.firstPost();
		const plainText = (firstPost && firstPost.contentPlain()) || '';

		const review = discussion.review();
		const article = review && review.article();
		const coverImage = (article && article.coverImage()) || '';
		const rating = (review && review.rating()) || 0;

		const routeToReview = app.route.discussion(discussion);

		return (
			<div {...attrs}>
				{!!controls.length && (
					<Dropdown
						icon="fas fa-ellipsis-v"
						className="DiscussionListItem-controls"
						buttonClassName="Button Button--icon Button--flat Slidable-underneath Slidable-underneath--right"
						accessibleToggleLabel={app.translator.trans('core.forum.discussion_controls.toggle_dropdown_accessible_label')}
					>
						{controls}
					</Dropdown>
				)}
				<div className="DiscussionListItem-content">
					{this.attrs.noCover !== true && <ReviewItemCover coverImage={coverImage} routeToReview={routeToReview} isLink={!this.attrs.linkAll} />}
					<div className="ReviewItem-content">
						<div className="ReviewItem-header-row1">
							<ArticleRating value={rating} />
							<span>{humanTime(discussion.createdAt())}</span>
						</div>
						<div className="ReviewItem-header-row2">
							<h3 className="DiscussionListItem-title">{discussion.title()}</h3>
							<div className="ReviewItem-author">
								{avatar(user)}
								{app.translator.trans('glowingblue-orell-rezensionen.forum.reviews.from_text', { user })}
							</div>
						</div>
						{this.attrs.expandable ? (
							<ExpandableContent
								body={
									<Link className="ReviewItem-snippet-link" href={routeToReview}>
										<p className="ReviewItem-snippet">{plainText}</p>
									</Link>
								}
								footer={<LinkButton href={routeToReview}>{app.translator.trans('glowingblue-orell-rezensionen.forum.reviews.read_more')}</LinkButton>}
							/>
						) : (
							<p className="ReviewItem-snippet">{plainText}</p>
						)}
					</div>
				</div>
			</div>
		);
	}
}
